import { Option } from "api/types/_public";
import { IWallet } from "api/types/_wallet";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownStatus from "components/Common/DropdownStatus";
import LabelWrapper from "components/Common/LabelWrapper";
import TableContainer from "components/Common/TableContainer";
import { customToFixed, formatNumberWithCommas, replaceHalfWithStar } from "helpers/format";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, CardHeader, Col, Input, Row, Spinner } from "reactstrap";
import { createSelector } from "reselect";
import {
    NumberParam,
    StringParam,
    useQueryParams,
    withDefault,
} from "use-query-params";
import { deleteWallet, getOptionPools as onGetOptionPools, getWallets as onGetWallets, postWallet } from "../../store/thunks";
import WalletForm from "./WalletForm";
import { toast, ToastContainer } from "react-toastify";
import CopyWrapper from "components/Common/CopyWrapper";
import SwapForm from "./SwapForm";
import ModalConfirm from "components/Common/ModalConfirm";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import LiquidityForm from "./LiquidityForm";
const TYPE_SELECT_DEFAULT: string = "id";



// bash$ export ANCHOR_PROVIDER_URL=<YOUR RPC ENDPOINT>
// bash$ export ANCHOR_WALLET=<YOUR WALLET JSON FILEPATH>
// bash$ ts-node this_script.ts



const AllTransactions = () => {
    const { t, i18n } = useTranslation();

    const TYPE_TRANSACTION_OPTIONS_LANG: any = [
        { value: 'Send', label: 'Send' },
        { value: 'Receive', label: 'Receive' },
        { value: 'Swap', label: 'Swap' },
        { value: 'Stake', label: 'Stake' },
        { value: 'Unstake', label: 'Unstake' },
        { value: 'Approve', label: 'Approve' },
        { value: 'Contract Call', label: 'Contract Call' },
        { value: 'Mint', label: 'Mint' },
        { value: 'Burn', label: 'Burn' },
        { value: 'Bridge', label: 'Bridge' },
    ]?.map((item: any) => ({ value: item?.value, label: t(item?.label) }));

    const STATUS_TRANSACTION_OPTIONS_LANG: any = [
        { value: 'Pending', label: 'Pending' },
        { value: 'Success', label: 'Success' },
        { value: 'Failed', label: 'Failed' },
        { value: 'Dropped', label: 'Dropped' },
        { value: 'Reverted', label: 'Reverted' },
        { value: 'Cancelled', label: 'Cancelled' }
    ]?.map((item: any) => ({ value: item?.value, label: t(item?.label) }));


    const COLOR_STATUS_TRANSACTION_OPTIONS = {
        "Pending": "#FFA500",
        "Success": "#28A745",
        "Failed": "#DC3545",
        "Dropped": "#6C757D",
        "Reverted": "#FF6F61",
        "Cancelled": "#ADB5BD"
    };

    const COLOR_TYPE_TRANSACTION_OPTIONS = {
        "Send": "#FF6B6B",
        "Receive": "#4ECDC4",
        "Swap": "#FFD93D",
        "Stake": "#845EC2",
        "Unstake": "#D65DB1",
        "Approve": "#00C9A7",
        "Contract Call": "#2C73D2",
        "Mint": "#00B8A9",
        "Burn": "#FF3F3F",
        "Bridge": "#F9A826"
    };

    const [query, setQuery]: any = useQueryParams({
        time_request: withDefault(NumberParam, 0),
        page: withDefault(NumberParam, 1),
        limit: withDefault(NumberParam, 100),
        sort_by: withDefault(StringParam, TYPE_SELECT_DEFAULT),
        // name: withDefault(StringParam, ""),
        // type: withDefault(StringParam, ""),
        // status: withDefault(StringParam, ""),
        // date_start: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
        // date_end: withDefault(StringParam, `${moment().format("Y-MM-DD")}`),
    });

    const [UUIDFilter, setUUIDFilter] = useState<string>('');
    const [typeSearch, setTypeSearch] = useState<Option | null>(TYPE_TRANSACTION_OPTIONS_LANG?.find((item: any) => String(item?.value) === String(query?.type)) || null);
    const [statusSearch, setStatusSearch] = useState<Option | null>(STATUS_TRANSACTION_OPTIONS_LANG?.find((item: any) => String(item?.value) === String(query?.status)) || null);

    const [dateRangeSearch, setDateRangeSearch] = useState<any[]>([query?.date_start ? moment(query?.date_start, 'Y-MM-DD').toDate() : null, query?.date_end ? moment(query?.date_end || '', 'Y-MM-DD').toDate() : null]);
    const [startDate, endDate] = dateRangeSearch;

    const [walletDetail, setWalletDetail] = useState<IWallet | null>(null);

    const [isOpenSwapFrom, setIsOpenSwapFrom] = useState<boolean>(false);

    const [isOpenLiquidityFrom, setIsOpenLiquidityFrom] = useState<boolean>(false);

    const [isOpenConfirmCreateWallet, setIsOpenConfirmCreateWallet] = useState<boolean>(false);
    const [isConfirmCreateWalletLoading, setIsConfirmCreateWalletLoading] = useState<boolean>(false);

    const [isOpenConfirmDeleteWallet, setIsOpenConfirmDeleteWallet] = useState<boolean>(false);
    const [isConfirmDeleteWalletLoading, setIsConfirmDeleteWalletLoading] = useState<boolean>(false);
    const [idDeleteWallet, setIdDeleteWallet] = useState<string | number | null>('');

    const dispatch: any = useDispatch();

    const selectLayoutStateWallet = (state: any) => {
        return state.Wallet;
    };

    const selectLayoutStatePool = (state: any) => {
        return state.Pool;
    };

    const WalletProperties = createSelector(selectLayoutStateWallet, (state) => ({
        wallets: state.wallets,
        isWalletSuccess: state.isWalletSuccess,
        isWalletLoading: state.isWalletLoading,
        error: state.error,
    }));

    // Inside your component
    const { wallets, error, isWalletLoading } = useSelector(WalletProperties);

    useEffect(() => {
        dispatch(onGetWallets(query));
    }, [dispatch, JSON.stringify(query)]);


    const PoolProperties = createSelector(selectLayoutStatePool, (state) => ({
        optionPools: state.optionPools || [],
    }));

    // Inside your component
    const { optionPools } = useSelector(PoolProperties);

    const [listPool, setListPool] = useState<any>([]);

    useEffect(() => {
        dispatch(onGetOptionPools({}));
    }, [dispatch]);

    const searchData = () => {
        setQuery({
            ...query,
            type: typeSearch?.value,
            status: statusSearch?.value,
            date_start: startDate ? moment(new Date(startDate)).format("Y-MM-DD") : '',
            date_end: endDate ? moment(new Date(endDate)).format("Y-MM-DD") : '',
            page: 1,
            time_request: + new Date()
        });
    };

    const resetData = () => {
        const queryNew = {
            type: '',
            status: '',
            date_start: `${moment().format("Y-MM-DD")}`,
            date_end: `${moment().format("Y-MM-DD")}`,
            sort_by: TYPE_SELECT_DEFAULT,
            time_request: + new Date(),
            page: 1,
        };
        setQuery(queryNew, "push")
        setTypeSearch(null);
        setStatusSearch(null);
        setDateRangeSearch([moment(queryNew?.date_start || "", 'Y-MM-DD').toDate(), moment(queryNew?.date_end || "", 'Y-MM-DD').toDate()]);
    };


    const handleSetWalletDetail = (detail: IWallet | null) => {
        setWalletDetail((_prev) => detail);
    }

    const onCloseClick = () => {
        handleSetWalletDetail(null);
    }

    const triggerRefresh = async (type: string = '') => {
        onCloseClick();
        onCloseConfirmCreateWalletClick();
        onCloseConfirmDeleteWalletClick();
        setQuery({
            ...query,
            time_request: + new Date(),
        }, "push");
    };

    const onOpenConfirmCreateWalletClick = () => {
        setIsOpenConfirmCreateWallet(() => true);
    }

    const onCloseConfirmCreateWalletClick = () => {
        setIsOpenConfirmCreateWallet(() => false);
    }

    const onOpenConfirmDeleteWalletClick = (id: string | number) => {
        setIdDeleteWallet(() => id);
        setIsOpenConfirmDeleteWallet(() => true);
    }

    const onCloseConfirmDeleteWalletClick = () => {
        setIsOpenConfirmDeleteWallet(() => false);
    }

    const handleActionCreateWallet = async () => {
        try {
            if (!!isConfirmCreateWalletLoading) {
                return;
            }
            setIsConfirmCreateWalletLoading((_prev) => true);
            const response: any = await postWallet({});
            setIsConfirmCreateWalletLoading((_prev) => false);
            if (!!response?.data) {
                triggerRefresh();
                toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
            } else {
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsConfirmCreateWalletLoading((_prev) => false);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const handleActionDeleteWallet = async () => {
        try {
            if (!idDeleteWallet || !!isConfirmDeleteWalletLoading) {
                return;
            }
            setIsConfirmDeleteWalletLoading((_prev) => true);
            const response: any = await deleteWallet(idDeleteWallet);
            setIsConfirmDeleteWalletLoading((_prev) => false);
            if (!!response?.data) {
                setIdDeleteWallet(() => null);
                triggerRefresh();
                toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
            } else {
                toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsConfirmDeleteWalletLoading((_prev) => false);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    // Column
    const columns = useMemo(
        () => [
            {
                Header: t("ID"),
                accessor: "wallets.ID",
                filterable: true,
                sortable: false,
                thWidth: 50,
                Cell: (cell: any) => (
                    <>
                        <span style={{ minWidth: "50px" }}>{cell?.value}</span>
                    </>
                ),
            },
            // {
            //   Header: t("Logo"),
            //   accessor: "logo",
            //   filterable: true,
            //   sortable: false,
            //   thWidth: 160,
            //   thClass: "text-center",
            //   Cell: (cell: any) => {
            //     const item = cell?.row?.original;
            //     return (
            //       <>
            //         <div className="text-center m-auto w-[60px]">
            //           <img
            //             src={
            //               item?.logo || LINK_FOR_NO_IMAGE
            //             }
            //             style={{
            //               width: "60px",
            //               height: "60px",
            //               borderRadius: "30px",
            //             }}
            //             className="flex-shrink-0 object-fit-cover m-auto w-[60px] h-[60px] !rounded-full"
            //             alt={item?.keyword}
            //           />
            //         </div>
            //       </>
            //     );
            //   },
            // },
            // {
            //     Header: t('Status'),
            //     accessor: "status",
            //     filterable: false,
            //     sortable: false,
            //     thClass: 'text-center',
            //     thWidth: 80,
            //     Cell: (cell: any) => {
            //         const COLOR: any = {
            //             Internal: 'success',
            //             External: 'primary',
            //         };
            //         return (
            //             <div className="text-center" style={{ minWidth: '60px' }}>
            //                 <span className={`rounded-pill badge bg-${COLOR?.[cell?.value] || 'secondary'}`}>{TYPE_TRANSACTION_OPTIONS_LANG?.find((_i: any) => String(_i?.value) === String(cell?.value))?.label}</span>
            //             </div>
            //         );
            //     }
            // },
            // {
            //     Header: t('Type'),
            //     accessor: "type",
            //     filterable: false,
            //     sortable: false,
            //     thClass: 'text-center',
            //     thWidth: 80,
            //     Cell: (cell: any) => {
            //         const COLOR: any = {
            //             Internal: 'success',
            //             External: 'primary',
            //         };
            //         return (
            //             <div className="text-center" style={{ minWidth: '60px' }}>
            //                 <span className={`rounded-pill badge bg-${COLOR?.[cell?.value] || 'secondary'}`}>{TYPE_TRANSACTION_OPTIONS_LANG?.find((_i: any) => String(_i?.value) === String(cell?.value))?.label}</span>
            //             </div>
            //         );
            //     }
            // },
            // {
            //     Header: t("Amount"),
            //     accessor: "amount",
            //     filterable: true,
            //     sortable: false,
            //     thWidth: 100,
            //     thClass: "text-end",
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 <div style={{ minWidth: "80px" }} className="text-end fs-15 bold">
            //                     ${formatNumberWithCommas(item?.fullyDilutedValue)}
            //                 </div>
            //             </>
            //         );
            //     },
            // },
            {
                Header: t("UUID"),
                accessor: "wallets.user_uuid",
                filterable: true,
                sortable: false,
                thWidth: 370,
                thClass: "text-start",
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-start" style={{ minWidth: "350px" }}>{cell?.value || "-"}</div>
                        </>
                    );
                },
            },
            {
                Header: t("Public Key"),
                accessor: "wallets.pubkey",
                filterable: true,
                sortable: false,
                thWidth: 450,
                thClass: "text-start",
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    return (
                        <>
                            <div className="text-start text-inline" style={{ minWidth: "430px" }}>
                                <CopyWrapper contentCopy={cell?.value} isShow={true}>
                                    {String(item?.wallets?.types) === '1' ? <i className="ri-star-fill text-warning fs-16"></i> : <></>}  {cell?.value || ''}
                                </CopyWrapper>
                            </div>
                        </>
                    );
                },
            },
            {
                Header: t("Balances"),
                accessor: "balances",
                filterable: true,
                sortable: false,
                thWidth: 150,
                thClass: "text-end",
                Cell: (cell: any) => {
                    const item = cell?.row?.original;
                    const _balance = Number(cell?.value) / Math.pow(10, 9);
                    return (
                        <>
                            <div className="text-end" style={{ minWidth: "130px" }}>
                                <div className="font-semibold d-flex flex-column align-items-end justify-content-end">
                                    <div className="text-primary">{customToFixed(_balance, 4)}</div>
                                    <div className=" d-flex align-items-center opacity-90">
                                        <span className="text-muted font-normal me-1 fs-14"> SOL</span>
                                        <img src="https://logo.moralis.io/solana-mainnet_So11111111111111111111111111111111111111112_5ffe40a76ba4f2a8.webp" alt="Token Icon" className="w-[14px] h-[14px] rounded-full" />
                                    </div>
                                </div>
                            </div>
                        </>
                    );
                },
            },
            // {
            //     Header: t("Private Key"),
            //     accessor: "privKey",
            //     filterable: true,
            //     sortable: false,
            //     thWidth: 200,
            //     thClass: "text-end",
            //     Cell: (cell: any) => {
            //         const item = cell?.row?.original;
            //         return (
            //             <>
            //                 <div className="text-end" style={{ minWidth: "170px" }}>
            //                     {!!cell?.value ? <CopyWrapper contentCopy={cell?.value} isShow={true}>
            //                         {(cell?.value || '')?.slice(0, 4) +
            //                             "********" +
            //                             (cell?.value || '')?.slice(-3)}
            //                     </CopyWrapper> : '-'}
            //                 </div>
            //             </>
            //         );
            //     },
            // },
            {
                Header: t("Created at"),
                accessor: "wallets.CreatedAt",
                filterable: true,
                sortable: false,
                thWidth: 130,
                thClass: "text-end",
                Cell: (cell: any) => {
                    const arrDate = String(cell?.value || "").split("T");
                    return (
                        <div className="text-end" style={{ minWidth: "120px" }}>
                            <span>
                                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                                <span className="text-secondary">{String(arrDate?.[1] || "")?.split('.')[0]}</span>{" "}
                            </span>
                        </div>
                    );
                },
            },
            {
                Header: t("Updated at"),
                accessor: "wallets.UpdatedAt",
                filterable: true,
                sortable: false,
                thWidth: 140,
                thClass: "text-end",
                Cell: (cell: any) => {
                    const arrDate = String(cell?.value || "").split("T");
                    return (
                        <div className="text-end pe-2" style={{ minWidth: "120px" }}>
                            <span>
                                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                                <span className="text-secondary">{String(arrDate?.[1] || "")?.split('.')[0]}</span>{" "}
                            </span>
                        </div>
                    );
                },
            },
            {
                Header: t('Action'),
                thWidth: 70,
                thClass: 'text-center align-middle',
                Cell: (cell: any) => {
                    const item: any = cell?.row?.original;
                    return (
                        <ul className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0" style={{ minWidth: '50px' }}>
                            <TooltipCustom
                                title={t('Delete Wallet')}
                                id={`detail-cp-${item?.wallets?.ID}`}
                            >
                                <li className="list-inline-item" id={`detail-cp-${item?.wallets?.ID}`}>
                                    <Link className="btn btn-sm btn-soft-danger edit-item-btn" to="#"
                                        onClick={(e) => { e.preventDefault(); onOpenConfirmDeleteWalletClick(item?.wallets?.ID) }}
                                    >
                                        <i className="ri-delete-bin-3-fill align-bottom"></i>
                                    </Link>
                                </li>
                            </TooltipCustom>
                        </ul>
                    );
                },
            },
        ],
        [i18n?.language, query]
    );

    const handleChangePage = useCallback((page: any) => {
        setQuery({ page: page + 1 });
    }, []);

    const handleChangeSorting = useCallback((sortBy: any) => {
        setQuery((_prev: any) => {
            return { ..._prev, ...sortBy };
        });
    }, []);

    const handleChangeDateRangePicker = (values: any[] = []) => {
        setDateRangeSearch((_prev: any) => values);
    };


    useEffect(() => {
        const list = (optionPools || [])?.map((item: any) => {
            return {
                value: item?.pairAddress,
                label: item?.pairLabel,
                logo1: item?.tokens?.[0]?.tokenLogo,
                logo2: item?.tokens?.[1]?.tokenLogo,
                tokenSymbol1: item?.tokens?.[0]?.tokenSymbol,
                tokenSymbol2: item?.tokens?.[1]?.tokenSymbol,
                tokenAddress1: item?.tokens?.[0]?.tokenAddress,
                tokenAddress2: item?.tokens?.[1]?.tokenAddress,
                tokenDecimals1: item?.tokens?.[0]?.tokenDecimals,
                tokenDecimals2: item?.tokens?.[1]?.tokenDecimals,
                pair: item?.pairLabel || '',
                tokenA: item?.tokenA,
                tokenB: item?.tokenB,
                aToB: item?.tokenA === item?.tokens?.[0]?.tokenAddress
            };
        });
        setListPool((_prev: any) => list);

    }, [optionPools]);

    return (
        <React.Fragment>
            {/* <Row className="align-items-center mb-4 g-3">
                <Col sm={2}>
                    <div className="d-flex align-items-center gap-2">
                    </div>
                </Col>
                <div className="col-sm-auto ms-auto">
                    <div className="d-flex gap-2">
                        <Link to="#" data-bs-toggle="modal" className="btn btn-info" >Deposite</Link>
                        <Link to="#" data-bs-toggle="modal" className="btn btn-success">Withdraw</Link>
                    </div>
                </div>
            </Row> */}

            <Card>
                <CardHeader>
                    <Row className="align-items-center g-3 py-3">
                        <Col sm={3} md={3} className="mt-0">
                            <h5 className="card-title mb-0">
                                <span className="me-2">{t("Total")}:</span>
                                {(isWalletLoading) ? <Spinner size="sm" className="text-primary"></Spinner> : (<CountUp
                                    start={0}
                                    end={wallets?.pagination?.total || 0}
                                    duration={1}
                                    className="text-primary"
                                />)}
                            </h5>
                        </Col>
                        <Col sm={9} md={9} className="text-end mt-0">
                            <div>
                                <button
                                    type="button"
                                    className="btn add-btn btn-warning  bg-[#ffc107] hover:bg-[#d9a406]"
                                    id="create-btn"
                                    onClick={() => {
                                        setIsOpenLiquidityFrom(true);
                                    }}
                                >
                                    <i className="ri-coins-line align-bottom me-1 "></i>
                                    {t('Liquidity')}
                                </button>
                                <button
                                    type="button"
                                    className="ms-3 btn btn-primary add-btn bg-[#009ef7] hover:bg-[#0086d2]"
                                    id="create-btn"
                                    onClick={() => {
                                        setIsOpenSwapFrom(true);
                                    }}
                                >
                                    <i className="ri-arrow-left-right-line align-bottom me-1 "></i>
                                    {t('Create Swap')}
                                </button>
                                <button
                                    type="button"
                                    className="ms-3 btn btn-success add-btn bg-[#50cd89] hover:bg-[#44ae74]"
                                    id="create-btn"
                                    onClick={() => {
                                        onOpenConfirmCreateWalletClick();
                                    }}
                                >
                                    <i className="ri-add-line align-bottom me-1"></i>
                                    {t('Create Wallet')}
                                </button>
                            </div>
                        </Col>
                    </Row>
                    <Row className="g-4 align-items-center mt-0 border border-dashed border-end-0 border-start-0 border-bottom-0 py-2">
                        {/* <Col sm={6} md={4} xl={3} xxl={2} className='mt-3'>
                            <LabelWrapper label={t('Status')} isShow={true}>
                                <DropdownStatus
                                    name="status"
                                    dataList={STATUS_TRANSACTION_OPTIONS_LANG || []}
                                    placeholder={`${t("Status")}...`}
                                    className="dropdown-status-rounded z-hight"
                                    classNamePrefix="name-prefix"
                                    initialValue={statusSearch || null}
                                    onChangeSelect={(e: any) => setStatusSearch(e)}
                                    isHasOptionAll={true}
                                    optionAll={{ label: t('All'), value: '' }}
                                    colors={COLOR_STATUS_TRANSACTION_OPTIONS}
                                    isColorObject={true}
                                    isColorStyle={true}
                                />
                            </LabelWrapper>
                        </Col>

                        <Col sm={6} md={4} xl={3} xxl={2} className='mt-3'>
                            <LabelWrapper label={t('Type')} isShow={true}>
                                <DropdownStatus
                                    name="type"
                                    dataList={TYPE_TRANSACTION_OPTIONS_LANG || []}
                                    placeholder={`${t("Type")}...`}
                                    className="dropdown-status-rounded z-hight"
                                    classNamePrefix="name-prefix"
                                    initialValue={typeSearch || null}
                                    onChangeSelect={(e: any) => setTypeSearch(e)}
                                    isHasOptionAll={true}
                                    optionAll={{ label: t('All'), value: '' }}
                                    colors={COLOR_TYPE_TRANSACTION_OPTIONS}
                                    isColorObject={true}
                                    isColorStyle={true}
                                />
                            </LabelWrapper>
                        </Col> */}

                        {/* <Col sm={6} md={4} xl={3} xxl={3} className='mt-3 date-picker-wrapper-custom'>
                            <LabelWrapper label={t('Date')} isShow={!!startDate || !!endDate}>
                                <DatePickerCustom
                                    placeholder={`${t('Start Date')} - ${t('End Date')}`}
                                    startDate={startDate || null}
                                    endDate={endDate || null}
                                    onChangePicker={handleChangeDateRangePicker}
                                    showOptions={[
                                        'today', 'yesterday', 'two_day_ago',
                                        'last_3_days', 'last_7_days', 'last_14_days', 'last_30_days',
                                        'this_week', 'last_week',
                                        'this_month', 'last_month',
                                    ]}
                                />
                            </LabelWrapper>
                        </Col> */}

                        <Col
                            sm={12}
                            md={12}
                            xl={3}
                            xxl={3}
                            className="mt-3 order-0"
                        >
                            <LabelWrapper label={t("UUID")} isShow={true}>
                                <Input
                                    type="text"
                                    className="form-control search"
                                    placeholder={`${t("UUID")}...`}
                                    value={UUIDFilter}
                                    onChange={(e) => setUUIDFilter(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            searchData();
                                        }
                                    }}
                                />
                            </LabelWrapper>
                        </Col>

                        <Col sm={12} md={12} xl={9} xxl={9} className="hstack gap-1 justify-content-center justify-content-sm-end justify-content-xl-start mt-3">
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-primary me-2  bg-[#009ef7] hover:bg-[#0086d2]"
                                    onClick={searchData}
                                    disabled={isWalletLoading}
                                >
                                    <i className="ri-search-line align-bottom me-1"></i>{" "}
                                    {t('Button Search')}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary fs-14  bg-[#878a99] hover:bg-[#737582]"
                                    onClick={resetData}
                                >
                                    <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                                    {t('Button Reset')}
                                </button>
                            </div>
                        </Col>
                    </Row>
                </CardHeader>
                <CardBody className="pt-3">
                    <TableContainer
                        className="custom-header-css"
                        divClass="table-responsive table-card"
                        tableClass="align-middle sticky-table-wallet"
                        theadClass="table-light"
                        columns={columns}
                        data={wallets?.list?.length ? wallets?.list : []}
                        customPageSize={query.limit}
                        customPageIndex={query.page - 1}
                        totalRecords={wallets?.pagination?.total}
                        // customPageCount={1}
                        customPageCount={Math.ceil(
                            Number(wallets?.pagination?.total) /
                            Number(wallets?.pagination?.limit)
                        )}
                        handleChangePage={handleChangePage}
                        manualSorting={true}
                        sorting={{
                            sort_by: query.sort_by,
                            order_by: query.order_by,
                        }}
                        handleChangeSorting={handleChangeSorting}
                        isLoading={isWalletLoading}
                    />
                </CardBody>
            </Card>

            <WalletForm
                isOpen={!!walletDetail}
                detail={walletDetail}
                listPool={listPool}
                onClose={onCloseClick}
                triggerRefresh={triggerRefresh}
            />

            <SwapForm
                isOpen={isOpenSwapFrom}
                listPool={listPool}
                onClose={() => setIsOpenSwapFrom(false)}
                triggerRefresh={() => setIsOpenSwapFrom(false)}
            />

            <LiquidityForm
                isOpen={isOpenLiquidityFrom}
                listPool={listPool}
                onClose={() => setIsOpenLiquidityFrom(false)}
                triggerRefresh={() => setIsOpenLiquidityFrom(false)}
            />

            <ModalConfirm
                header={t('Create Wallet')}
                textButtonConfirm={t('Create Wallet')}
                classButtonConfirm={'btn-success bg-[#50cd89] hover:bg-[#44ae74]'}
                title={t("Are you sure you want to create wallet?")}
                content={t("After confirming the create action, the data will be created. Do you want to proceed with the create action.")}
                isOpen={!!isOpenConfirmCreateWallet}
                classIconButtonConfirm=''
                isShowIcon={false}
                isLoading={isConfirmCreateWalletLoading}
                onClose={onCloseConfirmCreateWalletClick}
                onConfirm={handleActionCreateWallet}
            />

            <ModalConfirm
                header={t('Delete Wallet')}
                classButtonConfirm={'btn-danger bg-[#F8285A] hover:bg-[#d3224d]'}
                isOpen={isOpenConfirmDeleteWallet}
                isLoading={isConfirmDeleteWalletLoading}
                isShowIcon={false}
                classIconButtonConfirm=''
                onClose={onCloseConfirmDeleteWalletClick}
                onConfirm={handleActionDeleteWallet}
            />

            <ToastContainer autoClose={5000} limit={1} />
        </React.Fragment>
    );
};

export default AllTransactions;